import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDate, setFilterDate] = useState('7d');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchActivityLogs();
  }, [filterType, filterDate, sortOrder]);

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      const logs = [];
      const dateFilter = getDateFilter();

      // Fetch user profile changes (admin actions on users)
      if (filterType === 'all' || filterType === 'user_changes') {
        const { data: users, error: userError } = await supabase?.from('user_profiles')?.select('id, full_name, email, role, verified_status, created_at, updated_at')?.gte('updated_at', dateFilter)?.order('updated_at', { ascending: sortOrder === 'asc' });

        if (!userError && users) {
          users?.forEach(user => {
            logs?.push({
              id: `user-${user?.id}-${user?.updated_at}`,
              type: 'user_changes',
              action: user?.verified_status ? 'User Verified' : 'User Updated',
              target: user?.full_name || user?.email,
              details: `Role: ${user?.role}, Verified: ${user?.verified_status ? 'Yes' : 'No'}`,
              timestamp: user?.updated_at,
              actor: 'Admin',
              icon: 'UserCheck',
              color: 'text-primary'
            });
          });
        }
      }

      // Fetch dispute resolutions
      if (filterType === 'all' || filterType === 'dispute_resolutions') {
        const { data: disputes, error: disputeError } = await supabase?.from('rental_violation_charges')?.select(`
            id,
            charge_reference,
            charge_status,
            charge_amount,
            violation_reason,
            updated_at,
            renter:user_profiles!rental_violation_charges_renter_id_fkey(full_name, email)
          `)?.gte('updated_at', dateFilter)?.in('charge_status', ['paid', 'refunded', 'cancelled'])?.order('updated_at', { ascending: sortOrder === 'asc' });

        if (!disputeError && disputes) {
          disputes?.forEach(dispute => {
            logs?.push({
              id: `dispute-${dispute?.id}-${dispute?.updated_at}`,
              type: 'dispute_resolutions',
              action: `Dispute ${dispute?.charge_status}`,
              target: dispute?.charge_reference,
              details: `${dispute?.violation_reason} - $${dispute?.charge_amount} - ${dispute?.renter?.full_name || dispute?.renter?.email}`,
              timestamp: dispute?.updated_at,
              actor: 'Admin',
              icon: 'CheckCircle',
              color: getDisputeColor(dispute?.charge_status)
            });
          });
        }
      }

      // Fetch verification actions (driver licenses, insurance)
      if (filterType === 'all' || filterType === 'verification_actions') {
        const { data: licenses, error: licenseError } = await supabase?.from('driver_licenses')?.select(`
            id,
            verified,
            verified_at,
            user:user_profiles(full_name, email)
          `)?.gte('verified_at', dateFilter)?.eq('verified', true)?.order('verified_at', { ascending: sortOrder === 'asc' });

        if (!licenseError && licenses) {
          licenses?.forEach(license => {
            logs?.push({
              id: `license-${license?.id}-${license?.verified_at}`,
              type: 'verification_actions',
              action: 'Driver License Verified',
              target: license?.user?.full_name || license?.user?.email,
              details: 'Document verification completed',
              timestamp: license?.verified_at,
              actor: 'Admin',
              icon: 'ShieldCheck',
              color: 'text-success'
            });
          });
        }

        const { data: insurance, error: insuranceError } = await supabase?.from('insurance_verifications')?.select(`
            id,
            verified,
            verified_at,
            user:user_profiles(full_name, email)
          `)?.gte('verified_at', dateFilter)?.eq('verified', true)?.order('verified_at', { ascending: sortOrder === 'asc' });

        if (!insuranceError && insurance) {
          insurance?.forEach(ins => {
            logs?.push({
              id: `insurance-${ins?.id}-${ins?.verified_at}`,
              type: 'verification_actions',
              action: 'Insurance Verified',
              target: ins?.user?.full_name || ins?.user?.email,
              details: 'Insurance document verification completed',
              timestamp: ins?.verified_at,
              actor: 'Admin',
              icon: 'ShieldCheck',
              color: 'text-success'
            });
          });
        }
      }

      // Fetch booking status changes (admin interventions)
      if (filterType === 'all' || filterType === 'booking_actions') {
        const { data: bookings, error: bookingError } = await supabase?.from('bookings')?.select(`
            id,
            booking_reference,
            status,
            updated_at,
            renter:user_profiles!bookings_renter_id_fkey(full_name, email)
          `)?.gte('updated_at', dateFilter)?.in('status', ['cancelled', 'completed'])?.order('updated_at', { ascending: sortOrder === 'asc' });

        if (!bookingError && bookings) {
          bookings?.forEach(booking => {
            logs?.push({
              id: `booking-${booking?.id}-${booking?.updated_at}`,
              type: 'booking_actions',
              action: `Booking ${booking?.status}`,
              target: booking?.booking_reference,
              details: `User: ${booking?.renter?.full_name || booking?.renter?.email}`,
              timestamp: booking?.updated_at,
              actor: 'Admin',
              icon: 'Calendar',
              color: booking?.status === 'completed' ? 'text-success' : 'text-warning'
            });
          });
        }
      }

      // Sort all logs by timestamp
      logs?.sort((a, b) => {
        const dateA = new Date(a.timestamp);
        const dateB = new Date(b.timestamp);
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
      });

      setLogs(logs);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateFilter = () => {
    const now = new Date();
    const filters = {
      '24h': new Date(now.getTime() - 24 * 60 * 60 * 1000),
      '7d': new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
      '30d': new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
      '90d': new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000),
      'all': new Date('2020-01-01')
    };
    return filters?.[filterDate]?.toISOString() || filters?.['7d']?.toISOString();
  };

  const getDisputeColor = (status) => {
    const colors = {
      paid: 'text-success',
      refunded: 'text-primary',
      cancelled: 'text-muted-foreground'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  const filteredLogs = logs?.filter(log => {
    if (!searchTerm) return true;
    const searchLower = searchTerm?.toLowerCase();
    return (log?.action?.toLowerCase()?.includes(searchLower) ||
    log?.target?.toLowerCase()?.includes(searchLower) ||
    log?.details?.toLowerCase()?.includes(searchLower) || log?.actor?.toLowerCase()?.includes(searchLower));
  });

  const exportToCSV = () => {
    const headers = ['Timestamp', 'Type', 'Action', 'Target', 'Details', 'Actor'];
    const csvData = filteredLogs?.map(log => [
      new Date(log.timestamp)?.toLocaleString(),
      log?.type,
      log?.action,
      log?.target,
      log?.details,
      log?.actor
    ]);

    const csvContent = [
      headers?.join(','),
      ...csvData?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `activity-log-${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} strokeWidth={2} className="text-accent" />
          </div>
          <div>
            <h3 className="font-heading text-xl font-bold text-foreground">Activity Log</h3>
            <p className="font-body text-sm text-muted-foreground">Admin actions and system events</p>
          </div>
        </div>
        <Button
          onClick={exportToCSV}
          disabled={filteredLogs?.length === 0}
          className="flex items-center space-x-2"
        >
          <Icon name="Download" size={16} strokeWidth={2} />
          <span>Export CSV</span>
        </Button>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Search */}
        <div className="md:col-span-2">
          <Input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            icon="Search"
          />
        </div>

        {/* Type filter */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e?.target?.value)}
          className="px-4 py-2 rounded-md border border-border bg-card text-foreground font-body focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Types</option>
          <option value="user_changes">User Changes</option>
          <option value="dispute_resolutions">Dispute Resolutions</option>
          <option value="verification_actions">Verification Actions</option>
          <option value="booking_actions">Booking Actions</option>
        </select>

        {/* Date filter */}
        <select
          value={filterDate}
          onChange={(e) => setFilterDate(e?.target?.value)}
          className="px-4 py-2 rounded-md border border-border bg-card text-foreground font-body focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="all">All Time</option>
        </select>
      </div>
      {/* Sort controls */}
      <div className="flex items-center justify-between mb-4">
        <p className="font-body text-sm text-muted-foreground">
          {filteredLogs?.length} {filteredLogs?.length === 1 ? 'entry' : 'entries'} found
        </p>
        <button
          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
          className="flex items-center space-x-2 px-3 py-2 rounded-md bg-muted hover:bg-muted/80 transition-smooth"
        >
          <Icon
            name={sortOrder === 'desc' ? 'ArrowDown' : 'ArrowUp'}
            size={16}
            strokeWidth={2}
            className="text-foreground"
          />
          <span className="font-body text-sm text-foreground">
            {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
          </span>
        </button>
      </div>
      {/* Activity log table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-3 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="font-body text-muted-foreground">Loading activity logs...</span>
            </div>
          </div>
        ) : filteredLogs?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="font-body text-muted-foreground">No activity logs found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-body font-semibold text-foreground">Timestamp</th>
                <th className="text-left py-3 px-4 font-body font-semibold text-foreground">Type</th>
                <th className="text-left py-3 px-4 font-body font-semibold text-foreground">Action</th>
                <th className="text-left py-3 px-4 font-body font-semibold text-foreground">Target</th>
                <th className="text-left py-3 px-4 font-body font-semibold text-foreground">Details</th>
                <th className="text-left py-3 px-4 font-body font-semibold text-foreground">Actor</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs?.map((log) => (
                <tr key={log?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                  <td className="py-3 px-4 font-body text-sm text-muted-foreground whitespace-nowrap">
                    {formatTimestamp(log?.timestamp)}
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center space-x-2 px-2 py-1 rounded-md bg-muted text-xs font-body font-medium text-foreground">
                      <Icon name={log?.icon} size={14} strokeWidth={2} className={log?.color} />
                      <span className="capitalize">{log?.type?.replace('_', ' ')}</span>
                    </span>
                  </td>
                  <td className="py-3 px-4 font-body text-sm font-medium text-foreground">
                    {log?.action}
                  </td>
                  <td className="py-3 px-4 font-body text-sm text-foreground">
                    {log?.target}
                  </td>
                  <td className="py-3 px-4 font-body text-sm text-muted-foreground">
                    {log?.details}
                  </td>
                  <td className="py-3 px-4 font-body text-sm text-muted-foreground">
                    {log?.actor}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;